import { HttpClient } from '@angular/common/http';
import { computed, inject, Injectable } from '@angular/core';
import { BasketService } from '@basket';
import { CatalogueService } from '@catalogue';
import { buildMultiQuoteHttpOptions, generateId } from '@common';
import { CONFIG } from '@config';
import { CustomerService } from '@customer';
import { CoverDetailsService } from '@data-access';
import { map, Observable } from 'rxjs';
import { RegistrationApplianceDetails } from '../../models/registration/registration-appliance-details.interface';
import { RegistrationRequest } from '../../models/registration/registration-request.interface';
import { RegistrationResponse } from '../../models/registration/registration-response.interface';
import { formatBasketItemApplianceDetailsToRegistrationApplianceDetails } from '../../utils/checkout.utils';

@Injectable({
  providedIn: 'root',
})
export class RegistrationService {
  private readonly config = inject(CONFIG);
  private readonly catalogueService = inject(CatalogueService);
  private readonly basketService = inject(BasketService);
  private readonly commonEndpoint = this.config.commonApi;
  private readonly customerService = inject(CustomerService);
  private readonly httpClient = inject(HttpClient);
  private readonly coverDetailsService = inject(CoverDetailsService);

  private clpEnabled = Boolean(this.config.form?.clpEnabled);
  private multiPlanEnabled = Boolean(
    this.config.domainFeatures?.multiPlanEnabled,
  );

  // === Public Signals ===
  readonly registrationApplianceDetails =
    computed<RegistrationApplianceDetails>(() => {
      const applianceDetails = this.basketService.basketItemApplianceDetails();
      const quoteDetails = this.basketService.basketItemQuoteDetails();
      const basketItem = this.basketService.basketItem();

      return formatBasketItemApplianceDetailsToRegistrationApplianceDetails(
        applianceDetails,
        quoteDetails,
        basketItem,
        this.catalogueService.selectedAppliancePurchaseDate(),
      );
    });

  readonly noQuoteRegistationApplianceDetails =
    computed<RegistrationApplianceDetails>(() => {
      const appliance = this.catalogueService.steppedFormAppliance();
      const purchaseDate =
        this.catalogueService.selectedAppliancePurchaseDate();
      const purchasePrice =
        this.catalogueService.selectedAppliancePurchasePrice();
      const applianceWarranty =
        this.catalogueService.steppedFormApplianceWarranty();

      return {
        applianceCode: appliance?.applianceCode,
        applianceName: appliance?.applianceName,
        brandCode: this.config.clientBrandCode,
        brandName: this.config.clientName,
        purchaseDay: purchaseDate?.getDate(),
        purchaseMonth: purchaseDate?.getMonth() + 1,
        purchasePrice: purchasePrice,
        purchaseYear: purchaseDate?.getFullYear(),
        warranty: applianceWarranty,
        id: generateId(),
      } as RegistrationApplianceDetails;
    });

  readonly registrationRequest = computed<RegistrationRequest>(() => {
    const request: RegistrationRequest = {
      item: this.basketService.basket()
        ? this.registrationApplianceDetails()
        : this.noQuoteRegistationApplianceDetails(),
      person: this.customerService.customerDetails(),
      quoteId: this.basketService.retrievedQuoteId(),
      isRegOnly: !this.customerService.customerSelectedToPurchasePlan(),
    };

    if (this.shouldIncludeCoverDetails()) {
      request.coverDetails = this.coverDetailsService.getCoverDetails();
    }

    return request;
  });

  private readonly shouldIncludeCoverDetails = computed<boolean>(
    () =>
      !!this.basketService.basket() &&
      !this.basketService.isMultiPlanJourney() &&
      this.clpEnabled,
  );

  // === API calls ===
  registerAppliance(): Observable<RegistrationResponse> {
    const version =
      this.basketService.isMultiPlanJourney() || !this.clpEnabled ? 'v2' : 'v3';

    const options = buildMultiQuoteHttpOptions(
      this.multiPlanEnabled && this.basketService.isMultiPlanJourney(),
    );

    return this.httpClient
      .post<RegistrationResponse>(
        `${this.commonEndpoint}/${version}/register`,
        this.registrationRequest(),
        options,
      )
      .pipe(map((response: RegistrationResponse) => response));
  }
}
